import React, { useCallback, useContext, useMemo, useState, type FC } from "react";
import type { ColumnsType } from "antd/es/table";
import { useNavigate } from "react-router-dom";
import { Button, DataTable, SegmentedControl, StatusBadge } from "../../../../components/shared";
import CustomPagination from "../../../../components/shared/CustomPagination/CustomPagination";
import FeatureFlagGuard from "../../../FeatureFlagGuard/FeatureFlagGuard";
import AppContext from "../../../../context/AppContext";
import { usePamFeature } from "../../../../hooks/useFeatureFlag";
import { SIDER_TABS } from "../../../../utils/Constants";
import { NAVIGATION_ROUTES } from "../../../../utils/NavigationUtils";
import { isPermissionBypassActive } from "../../../../utils/permissionBypass";
import { useListCampaigns, useListCampaignsPage } from "../../../../queries/dynamicBonuses/useListCampaigns";
import { useListSeries } from "../../../../queries/dynamicBonuses/useListSeries";
import {
  DEFAULT_CAMPAIGN_TYPE,
  SERIES_CAMPAIGN_TYPE,
  DISPLAY_BANNER_RESULT_TYPE_KEY,
  campaignTypeLabel,
  inferTriggerModeFromStepSpecs,
  type WizardCampaignType,
} from "./CreateCampaignWizard/toWizardCampaignIn";
import {
  EMPTY_DISPLAY,
  LIST_TABLE_BANNER_COLUMN_WIDTH,
  LIST_TABLE_NAME_COLUMN_WIDTH,
} from "../constants";
import { formatListTableDateTime } from "../listTableDateTime";
import { triggerModeDisplayLabel } from "../triggerModeLabels";
import {
  DYNAMIC_BONUS_CAMPAIGN_STATUS,
  type DynamicBonusCampaignStatus,
  type CampaignOut,
  type SeriesOut,
  type StepSpecIn,
} from "../types";
import { compareIsoDates, compareNumbers } from "../listTableSorters";
import {
  CAMPAIGN_COLUMN_SORT_DIRECTIONS,
  DEFAULT_CAMPAIGN_SORT_BY,
  DEFAULT_CAMPAIGN_SORT_ORDER,
  resolveCampaignSortChange,
  serverSortColumnProps,
  type CampaignListSortBy,
  type CampaignListSortOrder,
} from "./campaignsTableSort";
import OfferPreview from "../controls/OfferPreview";
import CampaignRowActions from "./CampaignRowActions";
import SeriesRowActions from "./SeriesRowActions";
import SelectTemplateModal, {
  type CreateCampaignModalContinue,
} from "./SelectTemplateModal";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { usePageTitle } from "../../../CommonComponents/Layout/PageTitleContext";
import listStyles from "../../../Affiliates/Affiliates.module.css";
import styles from "../stylesDynamicBonuses.module.css";

const STATUS_SORT_ORDER: Record<DynamicBonusCampaignStatus, number> = {
  active: 0,
  draft: 1,
  paused: 2,
  expired: 3,
  archived: 4,
};

const STATUS_COLORS: Record<DynamicBonusCampaignStatus, string> = {
  active: "#2a7341",
  draft: "#62626f",
  paused: "#4f62c9",
  expired: "#d68910",
  archived: "#e74c3c",
};

const renderCampaignPreview = (row: CampaignsRow): React.ReactNode => {
  const previews = (row.steps ?? []).flatMap((step, stepIndex) => {
    const result =
      step.results.find((candidate) => candidate.type === DISPLAY_BANNER_RESULT_TYPE_KEY) ??
      step.results.find((candidate) => {
        const params = candidate.params ?? {};
        return Boolean(
          step.banner ||
            params.banner_title_key ||
            params.banner_msg_key,
        );
      });
    if (!step.banner && !result) return [];

    return [
      {
        key: `${step.step_id}-${stepIndex}`,
        result,
        banner: step.banner,
        layout: step.freeform_layout,
      },
    ];
  });

  const [firstPreview] = previews;
  if (!firstPreview) {
    return (
      <div className={`${styles.campaignPreviewCell} ${styles.campaignPreviewEmpty}`}>
        {EMPTY_DISPLAY}
      </div>
    );
  }

  const remainingCount = previews.length - 1;

  return (
    <div className={styles.campaignPreviewCell}>
      <div className={styles.campaignPreviewBanner}>
        <OfferPreview
          result={firstPreview.result}
          banner={firstPreview.banner}
          layout={firstPreview.layout}
          localizedStrings={row.localized_strings}
        />
      </div>
      {remainingCount > 0 ? (
        <span
          className={styles.campaignPreviewCount}
          aria-label={`${remainingCount} more preview${remainingCount === 1 ? "" : "s"}`}
        >
          +{remainingCount}
        </span>
      ) : (
        <span
          className={`${styles.campaignPreviewCount} ${styles.campaignPreviewCountPlaceholder}`}
          aria-hidden="true"
        />
      )}
    </div>
  );
};

/**
 * Normalized row shape for `CampaignOut` and `SeriesOut`.
 * Flag-off still merges series FE-side. Flag-on uses the paged campaigns
 * list with `include_series=true` and distinguishes series by `category`.
 */
type CampaignsRow = {
  id: string;
  rowType: WizardCampaignType;
  name: string;
  status: DynamicBonusCampaignStatus;
  starts_at: string;
  ends_at: string | null;
  priority?: number | string;
  segment_name?: string | null;
  steps: StepSpecIn[];
  localized_strings?: Record<string, Record<string, string>>;
  reached_users?: number;
  used_users?: number;
  /** used_users / reached_users × 100; undefined when either count is missing or reached is 0. */
  conversion_rate?: number;
};

/** Lifetime used÷reached conversion as a percent. Undefined when not computable. */
const conversionRate = (
  reached: number | undefined,
  used: number | undefined,
): number | undefined => {
  if (reached == null || used == null || reached === 0) return undefined;
  return (used / reached) * 100;
};

const formatConversionRate = (rate: number | undefined): string =>
  rate == null ? EMPTY_DISPLAY : `${Number(rate.toFixed(1))}%`;

const campaignTriggerLabel = (row: CampaignsRow): string => {
  if (row.rowType === SERIES_CAMPAIGN_TYPE) {
    return triggerModeDisplayLabel("campaign_start");
  }
  return triggerModeDisplayLabel(inferTriggerModeFromStepSpecs(row.steps ?? []));
};

const campaignToRow = (c: CampaignOut): CampaignsRow => {
  const seriesRowId =
    c.category === "series" ? c.series_campaign_id : undefined;
  return {
    id: seriesRowId || c.campaign_id,
    rowType: seriesRowId ? SERIES_CAMPAIGN_TYPE : DEFAULT_CAMPAIGN_TYPE,
    name: c.name,
    status: c.status,
    starts_at: c.starts_at,
    ends_at: c.ends_at ?? null,
    priority: c.priority,
    segment_name: c.segment_name,
    steps: c.steps,
    localized_strings: c.localized_strings,
    reached_users: c.reached_users,
    used_users: c.used_users,
    conversion_rate: conversionRate(c.reached_users, c.used_users),
  };
};

const seriesToRow = (s: SeriesOut): CampaignsRow => ({
  id: s.series_campaign_id,
  rowType: SERIES_CAMPAIGN_TYPE,
  name: s.name,
  status: s.status,
  starts_at: s.starts_at,
  ends_at: null,
  priority: s.priority,
  segment_name: undefined,
  steps: s.steps,
  localized_strings: s.localized_strings,
});

type ColumnSortMode =
  | { kind: "client" }
  | {
      kind: "server";
      sortBy: CampaignListSortBy;
      sortOrder: CampaignListSortOrder;
    };

const withColumnSort = (
  columnKey: string,
  mode: ColumnSortMode,
  clientSorter?: (a: CampaignsRow, b: CampaignsRow) => number,
) => {
  if (mode.kind === "server") {
    return serverSortColumnProps(columnKey, mode.sortBy, mode.sortOrder);
  }
  return clientSorter
    ? {
        sorter: clientSorter,
        sortDirections: CAMPAIGN_COLUMN_SORT_DIRECTIONS,
        showSorterTooltip: false,
      }
    : {};
};

const sourceSortRank = (rowType: CampaignsRow["rowType"]): number =>
  rowType === SERIES_CAMPAIGN_TYPE ? 1 : 0;

const buildColumns = (
  showType: boolean,
  showMetrics: boolean,
  sortMode: ColumnSortMode,
): ColumnsType<CampaignsRow> => {
  // Order: Banner → Name → … → Start/End (rightmost data cols) → actions
  const columns: ColumnsType<CampaignsRow> = [
    {
      title: AppStrings.PAM_DYNAMIC_BONUS_COLUMN_BANNER,
      key: "preview",
      width: LIST_TABLE_BANNER_COLUMN_WIDTH,
      ellipsis: false,
      className: styles.campaignPreviewColumn,
      render: (_: unknown, record: CampaignsRow) => renderCampaignPreview(record),
    },
    {
      title: AppStrings.NAME,
      dataIndex: "name",
      key: "name",
      width: LIST_TABLE_NAME_COLUMN_WIDTH,
      render: (name: string) => <span className={styles.campaignName}>{name}</span>,
    },
  ];

  columns.push({
    title: AppStrings.STATUS,
    dataIndex: "status",
    key: "status",
    width: 100,
    ellipsis: false,
    render: (v: CampaignsRow["status"]) => (
      <StatusBadge
        color={STATUS_COLORS[v]}
        variant="solid"
        className={styles.campaignStatusBadge}
      >
        {v}
      </StatusBadge>
    ),
    ...withColumnSort("status", sortMode, (a, b) =>
      STATUS_SORT_ORDER[a.status] - STATUS_SORT_ORDER[b.status],
    ),
  });

  if (showType) {
    columns.push({
      title: AppStrings.PAM_DYNAMIC_BONUS_COLUMN_SOURCE,
      dataIndex: "rowType",
      key: "rowType",
      width: 100,
      render: (v: CampaignsRow["rowType"]) =>
        v === SERIES_CAMPAIGN_TYPE ? (
          <StatusBadge variant="solid" className={styles.brazeTypeBadge}>
            {campaignTypeLabel(v)}
          </StatusBadge>
        ) : (
          <StatusBadge tone="neutral">{campaignTypeLabel(v)}</StatusBadge>
        ),
      ...withColumnSort("rowType", sortMode, (a, b) =>
        sourceSortRank(a.rowType) - sourceSortRank(b.rowType),
      ),
    });
  }

  columns.push({
    title: AppStrings.PAM_DYNAMIC_BONUS_COLUMN_TRIGGER,
    key: "trigger_type",
    width: 140,
    render: (_: unknown, row: CampaignsRow) => campaignTriggerLabel(row),
  });

  if (showMetrics) {
    columns.push(
      {
        title: AppStrings.PAM_DYNAMIC_BONUS_COLUMN_REACHED,
        dataIndex: "reached_users",
        key: "reached_users",
        width: 120,
        ellipsis: false,
        align: "right",
        className: styles.campaignMetricsColumn,
        render: (v: number | undefined) => (
          <span className={styles.campaignMetricsCell}>{v ?? EMPTY_DISPLAY}</span>
        ),
        ...withColumnSort("reached_users", sortMode, (a, b) =>
          compareNumbers(a.reached_users, b.reached_users),
        ),
      },
      {
        title: AppStrings.PAM_DYNAMIC_BONUS_COLUMN_USED,
        dataIndex: "used_users",
        key: "used_users",
        width: 100,
        align: "right",
        className: styles.campaignMetricsColumn,
        render: (v: number | undefined) => (
          <span className={styles.campaignMetricsCell}>{v ?? EMPTY_DISPLAY}</span>
        ),
        ...withColumnSort("used_users", sortMode, (a, b) =>
          compareNumbers(a.used_users, b.used_users),
        ),
      },
      {
        title: AppStrings.PAM_DYNAMIC_BONUS_COLUMN_CONVERSION,
        dataIndex: "conversion_rate",
        key: "conversion_rate",
        width: 90,
        align: "right",
        className: styles.campaignMetricsColumn,
        render: (v: number | undefined) => (
          <span className={styles.campaignMetricsCell}>{formatConversionRate(v)}</span>
        ),
        ...withColumnSort("conversion_rate", sortMode, (a, b) =>
          compareNumbers(a.conversion_rate, b.conversion_rate),
        ),
      },
    );
  }

  columns.push(
    {
      title: AppStrings.PAM_DYNAMIC_BONUS_COLUMN_CAMPAIGN_START,
      dataIndex: "starts_at",
      key: "starts_at",
      width: 140,
      ...(sortMode.kind === "client" ? { defaultSortOrder: "descend" as const } : {}),
      render: (v: string | null) => formatListTableDateTime(v),
      ...withColumnSort("starts_at", sortMode, (a, b) =>
        compareIsoDates(a.starts_at, b.starts_at),
      ),
    },
    {
      title: AppStrings.PAM_DYNAMIC_BONUS_COLUMN_CAMPAIGN_END,
      dataIndex: "ends_at",
      key: "ends_at",
      width: 140,
      render: (v: string | null) => formatListTableDateTime(v),
      ...withColumnSort("ends_at", sortMode, (a, b) =>
        compareIsoDates(a.ends_at, b.ends_at),
      ),
    },
    {
      title: "",
      key: "actions",
      width: 56,
      ellipsis: false,
      // Campaign rows get Duplicate/Pause/Expire. Series rows get the Braze
      // payload menu (BONENG-1006). No dedicated feature flag by design: this is
      // a read-only operator helper in the existing dynamicBonusesExternal-gated
      // series flow, with no campaign mutation, no end-user behavior change, and
      // no access expansion. A second flag here would be a redundant duplicate.
      render: (_: unknown, record: CampaignsRow) =>
        record.rowType === DEFAULT_CAMPAIGN_TYPE ? (
          <CampaignRowActions
            campaignId={record.id}
            name={record.name}
            status={record.status}
          />
        ) : (
          <SeriesRowActions seriesId={record.id} />
        ),
    },
  );

  return columns;
};

type StatusFilter = DynamicBonusCampaignStatus | "all";

const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const getStatusOptions = (): { label: string; value: StatusFilter }[] => [
  { label: AppStrings.ALL, value: "all" },
  { label: AppStrings.ACTIVE_FILTER_OPTION, value: "active" },
  { label: AppStrings.PAM_DYNAMIC_BONUS_STATUS_DRAFT, value: "draft" },
  { label: AppStrings.PAM_SEGMENTS_STATUS_PAUSED, value: "paused" },
  { label: AppStrings.EXPIRED, value: "expired" },
  { label: AppStrings.PAM_SEGMENTS_STATUS_ARCHIVED, value: "archived" },
];

const getStatusFilterItems = () =>
  getStatusOptions().map(({ label, value }) => ({
    id: value,
    label,
  }));

type AppContextShape = {
  adminPermissions?: string[] | null;
};

const CampaignsPage: FC = () => {
  usePageTitle(AppStrings.PAM_CAMPAIGNS);

  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sortBy, setSortBy] = useState<CampaignListSortBy>(DEFAULT_CAMPAIGN_SORT_BY);
  const [sortOrder, setSortOrder] = useState<CampaignListSortOrder>(
    DEFAULT_CAMPAIGN_SORT_ORDER,
  );
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  // Templates and Segments have no sider entry (BONENG-967); these buttons are
  // the only way in, so they mirror the permission their route already
  // enforces — including the `?bypassPermissions=true` break-glass, otherwise
  // the override would unblock the pages but not the links to them.
  const { adminPermissions } = (useContext(AppContext) ?? {}) as AppContextShape;
  const permissionBypass = isPermissionBypassActive();
  const hasPermission = (permission: string): boolean =>
    permissionBypass || Boolean(adminPermissions?.includes(permission));
  const canSeeTemplates = hasPermission("promotions.templates");
  const archivedStatus =
    statusFilter === DYNAMIC_BONUS_CAMPAIGN_STATUS.ARCHIVED
      ? DYNAMIC_BONUS_CAMPAIGN_STATUS.ARCHIVED
      : undefined;
  const { isEnabled: seriesEnabled } = usePamFeature("dynamicBonusesExternal");
  const { isEnabled: metricsEnabled } = usePamFeature("campaignMetrics");
  const { isEnabled: paginationEnabled } = usePamFeature("campaignListPagination");
  const { isEnabled: dynamicBonusesBetaMode } = usePamFeature(
    "dynamicBonusesBetaMode",
  );
  // Segment targeting is a beta surface — same gate as the campaign
  // wizard Segment card. Loading/null is treated as off (button hidden).
  const canSeeSegments =
    hasPermission("promotions.segments") && dynamicBonusesBetaMode === true;
  const pagedStatus =
    statusFilter === "all" ? undefined : statusFilter;
  const legacyQuery = useListCampaigns(archivedStatus, {
    enabled: !paginationEnabled,
  });
  const pagedQuery = useListCampaignsPage({
    status: pagedStatus,
    page,
    pageSize,
    includeSeries: Boolean(seriesEnabled),
    sortBy,
    sortOrder,
    enabled: Boolean(paginationEnabled),
  });
  // The backend fails closed (403) on this endpoint while the flag is off —
  // skip the request entirely rather than firing a guaranteed-failing call.
  // The paged campaigns list already merges series when include_series is on.
  const seriesQuery = useListSeries(
    Boolean(seriesEnabled) && !paginationEnabled,
    archivedStatus,
  );

  const columns = useMemo(
    () =>
      buildColumns(
        Boolean(seriesEnabled),
        Boolean(metricsEnabled),
        paginationEnabled
          ? { kind: "server", sortBy, sortOrder }
          : { kind: "client" },
      ),
    [seriesEnabled, metricsEnabled, paginationEnabled, sortBy, sortOrder],
  );

  const statusFilterItems = getStatusFilterItems();

  const rows = useMemo<CampaignsRow[]>(() => {
    if (paginationEnabled) {
      return (pagedQuery.data?.items ?? []).map(campaignToRow);
    }
    const campaignRows = (legacyQuery.data ?? []).map(campaignToRow);
    const seriesRows = seriesEnabled ? (seriesQuery.data ?? []).map(seriesToRow) : [];
    const all = [...campaignRows, ...seriesRows];
    return statusFilter === "all" ? all : all.filter((row) => row.status === statusFilter);
  }, [
    paginationEnabled,
    pagedQuery.data,
    legacyQuery.data,
    seriesEnabled,
    seriesQuery.data,
    statusFilter,
  ]);

  const isLoading = paginationEnabled
    ? Boolean(pagedQuery.isLoading || pagedQuery.isFetching)
    : Boolean(legacyQuery.isLoading);
  const isError = paginationEnabled ? pagedQuery.isError : legacyQuery.isError;
  const showSeriesError =
    Boolean(seriesEnabled) && !paginationEnabled && seriesQuery.isError;

  const handleCreateCampaignContinue = useCallback(
    ({ campaignType, template }: CreateCampaignModalContinue) => {
      const search = new URLSearchParams({ campaignType });
      if (template) {
        search.set("templateId", template.template_id);
      }
      setIsTemplateModalOpen(false);
      navigate(
        `${NAVIGATION_ROUTES.DYNAMIC_BONUS_CAMPAIGN_NEW}?${search.toString()}`,
      );
    },
    [navigate],
  );

  const handleTableChange = useCallback(
    (_pagination: unknown, _filters: unknown, sorter: unknown) => {
      const change = resolveCampaignSortChange({
        sorter,
        currentSortBy: sortBy,
        currentSortOrder: sortOrder,
      });
      if (change.type === "noop") {
        return;
      }
      setSortBy(change.sortBy);
      setSortOrder(change.sortOrder);
      setPage(1);
    },
    [sortBy, sortOrder],
  );

  return (
    <>
      <FeatureFlagGuard feature="dynamicBonuses" mode="route">
        <div className={`${listStyles.container} ${styles.campaignsContainer}`}>
          <div className={styles.campaignsToolbar}>
            <SegmentedControl
              items={statusFilterItems}
              value={statusFilter}
              onChange={(value: StatusFilter) => {
                setStatusFilter(value);
                setPage(1);
              }}
              ariaLabel={AppStrings.PAM_CAMPAIGNS_FILTER_ARIA_LABEL}
              className={styles.campaignStatusFilter}
              data-testid="status-filter"
            />

            <div className={styles.campaignsToolbarActions}>
              {canSeeTemplates && (
                <Button
                  intent="neutral"
                  variant="outline"
                  className={styles.campaignsToolbarLinkButton}
                  onClick={() => navigate(NAVIGATION_ROUTES.DYNAMIC_BONUS_TEMPLATES)}
                >
                  {AppStrings.PAM_TEMPLATES}
                </Button>
              )}

              {canSeeSegments && (
                <Button
                  intent="neutral"
                  variant="outline"
                  className={styles.campaignsToolbarLinkButton}
                  onClick={() => navigate(NAVIGATION_ROUTES.DYNAMIC_BONUS_SEGMENTS)}
                >
                  {AppStrings.PAM_SEGMENTS}
                </Button>
              )}

              <Button
                intent="primary"
                className={styles.createCampaignButton}
                onClick={() => setIsTemplateModalOpen(true)}
              >
                {AppStrings.CREATE_CAMPAIGN}
              </Button>
            </div>
          </div>

          <div className={styles.campaignsTableSection}>
            {isError && (
              <p className={styles.listError}>{AppStrings.PAM_CAMPAIGNS_LOAD_ERROR}</p>
            )}
            {showSeriesError && (
              <p className={styles.listError}>{AppStrings.PAM_CAMPAIGNS_SERIES_LOAD_ERROR}</p>
            )}

            <DataTable<CampaignsRow>
              rowKey="id"
              columns={columns}
              dataSource={rows}
              loading={
                isLoading ||
                (!paginationEnabled && Boolean(seriesEnabled) && seriesQuery.isLoading)
              }
              pagination={false}
              sortDirections={CAMPAIGN_COLUMN_SORT_DIRECTIONS}
              className={styles.campaignsTable}
              onChange={paginationEnabled ? handleTableChange : undefined}
              onRow={(record) => ({
                onClick: () =>
                  navigate(
                    record.rowType === SERIES_CAMPAIGN_TYPE
                      ? NAVIGATION_ROUTES.DYNAMIC_BONUS_SERIES_DETAIL.replace(
                          ":seriesId",
                          record.id,
                        )
                      : NAVIGATION_ROUTES.DYNAMIC_BONUS_CAMPAIGN_DETAIL.replace(
                          ":campaignId",
                          record.id,
                        ),
                  ),
                style: { cursor: "pointer" },
              })}
            />
            {paginationEnabled ? (
              <CustomPagination
                total={pagedQuery.data?.total ?? 0}
                pageSize={pageSize}
                currentPage={page}
                onPageSizeChange={(value: number) => {
                  setPage(1);
                  setPageSize(value);
                }}
                onPageChange={(nextPage: number) => setPage(nextPage)}
                itemLabel="campaigns"
                pageSizeOptions={PAGE_SIZE_OPTIONS}
              />
            ) : null}
          </div>
        </div>

        {isTemplateModalOpen && (
          <SelectTemplateModal
            open
            onClose={() => setIsTemplateModalOpen(false)}
            onContinue={handleCreateCampaignContinue}
          />
        )}
      </FeatureFlagGuard>
    </>
  );
};

export default CampaignsPage;
